/**
 * Outbound webhook endpoints (Feature 50).
 *
 * One module for both owners: the prefix differs, everything below it is
 * identical, and two near-copies would drift. The caller always knows
 * which record it is on, so it passes the owner rather than choosing a
 * function.
 *
 * The secret comes back from `create` (when signing) and from `rotate`,
 * and from nowhere else — show it once and stop.
 */
import { apiDelete, apiGet, apiPost, apiPut } from '../client.js';
import type {
  UpsertWebhookEndpointBody,
  WebhookEndpoint,
  WebhookEndpointWithSecret,
  WebhookOwner,
  WebhookPing,
} from '../types.js';

function base(owner: WebhookOwner): string {
  const prefix =
    owner.kind === 'organization'
      ? `/api/v1/admin/organizations/${owner.id}`
      : `/api/v1/admin/federated-partners/${owner.id}`;
  return `${prefix}/webhook-endpoint`;
}

export function getWebhookEndpoint(owner: WebhookOwner): Promise<WebhookEndpoint> {
  return apiGet<WebhookEndpoint>(base(owner));
}

export function setWebhookEndpoint(
  owner: WebhookOwner,
  body: UpsertWebhookEndpointBody,
): Promise<WebhookEndpoint | WebhookEndpointWithSecret> {
  return apiPut<WebhookEndpoint | WebhookEndpointWithSecret>(base(owner), body);
}

export function deleteWebhookEndpoint(owner: WebhookOwner): Promise<void> {
  return apiDelete<void>(base(owner));
}

export function rotateWebhookSecret(
  owner: WebhookOwner,
): Promise<WebhookEndpointWithSecret> {
  return apiPost<WebhookEndpointWithSecret>(`${base(owner)}/rotate-secret`, {});
}

/** Drop signing. Deliveries then carry no signature header at all. */
export function dropWebhookSigning(owner: WebhookOwner): Promise<void> {
  return apiDelete<void>(`${base(owner)}/secret`);
}

export function testWebhookEndpoint(owner: WebhookOwner): Promise<WebhookPing> {
  return apiPost<WebhookPing>(`${base(owner)}/test`, {});
}
